<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { DriveNs } from '@shared/types'
import { useIntegrationStore } from '@domains/settings/stores/integration.store'
import { useWorkspaceStore } from '@shared/stores/workspace.store'
import ButtonElement from '@shared/components/elements/button/button.element.vue'
import CheckboxElement from '@shared/components/elements/checkbox/checkbox.element.vue'
import {toast} from '@shared/ms-components'

const props = defineProps<{
  projectId?: string
}>()

const emit = defineEmits<{
  (e: 'file-imported', response: DriveNs.IngestionResponse): void
}>()

const workspaceStore = useWorkspaceStore()
const integrationStore = useIntegrationStore()

const DRIVE_DOC_SIZE_LIMIT = 50 * 1024 * 1024 // 50 MB

const isLoading = ref(false)
const isImporting = ref(false)
const files = ref<DriveNs.DriveFile[]>([])
const nextPageToken = ref<string | undefined>(undefined)
const selectedIds = ref<string[]>([])
const errorMessage = ref<string | null>(null)

const breadcrumb = ref<Array<{ id: string; name: string }>>([
  { id: '', name: 'My Drive' },
])

const currentFolderId = computed<string | undefined>(() => {
  const last = breadcrumb.value.at(-1)
  return last?.id || undefined
})

const hasMore = computed(() => !!nextPageToken.value)

const selectedCount = computed(() => selectedIds.value.length)

async function loadFiles(append = false): Promise<void> {
  if (!append) {
    files.value = []
    nextPageToken.value = undefined
    selectedIds.value = []
  }
  isLoading.value = true
  errorMessage.value = null
  try {
    const response = await integrationStore.listDriveFiles({
      workspaceId: workspaceStore.getSelectedWorkspaceId,
      folderId: currentFolderId.value,
      pageToken: append ? nextPageToken.value : undefined,
    })
    if (append) {
      files.value.push(...response.files)
    } else {
      files.value = response.files
    }
    nextPageToken.value = response.nextPageToken
  } catch {
    errorMessage.value = 'Failed to load files. Please try again.'
  } finally {
    isLoading.value = false
  }
}

function onFolderClick(folder: DriveNs.DriveFile): void {
  breadcrumb.value.push({ id: folder.id, name: folder.name })
  loadFiles()
}

function navigateToBreadcrumb(index: number): void {
  if (index >= breadcrumb.value.length - 1) return
  breadcrumb.value = breadcrumb.value.slice(0, index + 1)
  loadFiles()
}

function toggleSelect(fileId: string): void {
  const idx = selectedIds.value.indexOf(fileId)
  if (idx !== -1) {
    selectedIds.value.splice(idx, 1)
  } else {
    selectedIds.value.push(fileId)
  }
}

async function loadMore(): Promise<void> {
  await loadFiles(true)
}

async function importSelected(): Promise<void> {
  if (!selectedIds.value.length) return
  isImporting.value = true
  try {
    for (const fileId of selectedIds.value) {
      const file = files.value.find((f) => f.id === fileId)
      if (!file) continue
      const fileSize = parseInt(file.size ?? '0', 10)
      if (fileSize > DRIVE_DOC_SIZE_LIMIT) {
        toast.error(`"${file.name}" exceeds the 50 MB import limit. Please use a smaller file.`)
        continue
      }
      const response = await integrationStore.importDriveFileToRag({
        workspaceId: workspaceStore.getSelectedWorkspaceId,
        projectId: props.projectId,
        fileId: file.id,
        mimeType: file.mimeType,
      })
      toast.success(`"${file.name}" imported — ${response.totalChunks} chunks created.`)
      emit('file-imported', response)
    }
    selectedIds.value = []
  } catch {
    toast.error('Import failed. Please try again.')
  } finally {
    isImporting.value = false
  }
}

onMounted(() => {
  loadFiles()
})
</script>

<template>
  <div class="drive-browser">
    <nav class="drive-browser__breadcrumb" aria-label="Folder navigation">
      <span
        v-for="(crumb, index) in breadcrumb"
        :key="crumb.id || 'root'"
        class="drive-browser__crumb"
      >
        <span
          :class="['drive-browser__crumb-label', { 'drive-browser__crumb-label--link': index < breadcrumb.length - 1 }]"
          @click="navigateToBreadcrumb(index)"
        >
          {{ crumb.name }}
        </span>
        <span v-if="index < breadcrumb.length - 1" class="drive-browser__crumb-sep">
          <fa :icon="['far', 'chevron-right']" />
        </span>
      </span>
    </nav>

    <div v-if="isLoading && !files.length" class="drive-browser__loading">
      <fa :icon="['far', 'spinner']" spin />
      Loading files…
    </div>

    <div v-else-if="errorMessage" class="drive-browser__error">
      <fa :icon="['far', 'triangle-exclamation']" />
      {{ errorMessage }}
      <ButtonElement class="btn--ms btn--accent btn--regular" @clickOnButton="loadFiles()">
        Retry
      </ButtonElement>
    </div>

    <div v-else-if="!isLoading && !files.length" class="drive-browser__empty">
      <fa :icon="['far', 'folder-open']" />
      <p>This folder is empty.</p>
    </div>

    <ul v-else class="drive-browser__list">
      <li
        v-for="file in files"
        :key="file.id"
        class="drive-browser__item"
        :class="{ 'drive-browser__item--folder': file.isFolder }"
        @click="file.isFolder ? onFolderClick(file) : toggleSelect(file.id)"
      >
        <span v-if="!file.isFolder" class="drive-browser__checkbox" @click.stop>
          <CheckboxElement
            :name="`file-${file.id}`"
            :value="file.id"
            :model-value="selectedIds"
            @update:model-value="(val: string[]) => { selectedIds = val }"
          />
        </span>
        <span v-else class="drive-browser__folder-icon">
          <fa :icon="['far', 'folder']" />
        </span>

        <img
          v-if="file.iconLink"
          :src="file.iconLink"
          :alt="file.mimeType"
          class="drive-browser__mime-icon"
        />
        <fa v-else :icon="['far', file.isFolder ? 'folder' : 'file']" class="drive-browser__mime-icon" />

        <span class="drive-browser__name">{{ file.name }}</span>

        <span class="drive-browser__meta">
          {{ file.modifiedTime ? new Date(file.modifiedTime).toLocaleDateString() : '' }}
        </span>

        <fa v-if="file.isFolder" :icon="['far', 'chevron-right']" class="drive-browser__chevron" />
      </li>
    </ul>

    <div v-if="hasMore && !isLoading" class="drive-browser__load-more">
      <ButtonElement class="btn--ms btn--neutral btn--regular" @clickOnButton="loadMore">
        Load more
      </ButtonElement>
    </div>

    <div class="drive-browser__footer">
      <ButtonElement
        class="btn--ms btn--accent btn--md"
        :disabled="selectedCount === 0 || isImporting"
        :class="{ 'btn--loading': isImporting }"
        @clickOnButton="importSelected"
      >
        <fa :icon="['far', 'cloud-arrow-down']" />
        {{ isImporting ? 'Importing…' : `Import to RAG${selectedCount ? ` (${selectedCount})` : ''}` }}
      </ButtonElement>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.drive-browser {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  min-height: 200px;

  &__breadcrumb {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    font-size: var(--fs-12);
    color: var(--text-neutral);
    flex-wrap: wrap;
    padding-bottom: var(--space-8);
    border-bottom: 1px solid var(--border-neutral);
  }

  &__crumb {
    display: flex;
    align-items: center;
    gap: var(--space-4);
  }

  &__crumb-label {
    &--link {
      cursor: pointer;
      color: var(--text-blue);
      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__crumb-sep {
    font-size: var(--fs-10);
    color: var(--text-neutral);
  }

  &__loading,
  &__error,
  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-8);
    padding: var(--space-32);
    color: var(--text-neutral);
    font-size: var(--fs-14);
    text-align: center;
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    max-height: 340px;
    overflow-y: auto;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--space-8);
    padding: var(--space-6) var(--space-8);
    border-radius: $br-xs;
    cursor: pointer;
    font-size: var(--fs-13);
    color: var(--text-dark);
    transition: background 0.1s;

    &:hover {
      background: var(--light-100);
    }

    &--folder {
      font-weight: 500;
    }
  }

  &__checkbox {
    flex: 0 0 16px;
  }

  &__folder-icon {
    flex: 0 0 16px;
    color: var(--text-neutral);
    font-size: var(--fs-14);
  }

  &__mime-icon {
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    object-fit: contain;
    color: var(--text-neutral);
    font-size: var(--fs-14);
  }

  &__name {
    flex: 1 1 auto;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__meta {
    flex: 0 0 auto;
    font-size: var(--fs-11);
    color: var(--text-neutral);
  }

  &__chevron {
    flex: 0 0 10px;
    font-size: var(--fs-10);
    color: var(--text-neutral);
  }

  &__load-more {
    display: flex;
    justify-content: center;
    padding-top: var(--space-8);
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    padding-top: var(--space-8);
    border-top: 1px solid var(--border-neutral);
    margin-top: auto;
  }
}
</style>
